import {
  initVersionedSchema,
  toTextContent,
  type RawInlineNodes,
} from '@principle-theorem/editor';
import {
  EventType,
  GapOfferStatus,
  IAppointment,
  ICalendarEvent,
  ICandidate,
  ICandidateCalendarEvent,
  IEvent,
  IParticipant,
  IPatient,
  IPractice,
  IScheduleSummaryEventable,
  IStaffer,
  MentionResourceType,
  ParticipantType,
  PrincipleCreatedBy,
  isAppointmentRequest,
  isCalendarEvent,
  type Permission,
} from '@principle-theorem/principle-core/interfaces';
import {
  AtLeast,
  DocumentReference,
  INamedDocument,
  ITimePeriod,
  MockTimestamp,
  WithRef,
  initFirestoreModel,
  isEnumValue,
  isSameRef,
  isWithRef,
  toTimestamp,
} from '@principle-theorem/shared';
import {
  MockDocRef,
  MockNamedDocument,
  MockReffable,
} from '@principle-theorem/testing';
import { isString } from 'lodash';
import * as moment from 'moment-timezone';
import { CALENDAR_EVENT_TYPE_PERMISSIONS } from './calendar-event-permissions';

export class CalendarEvent {
  static init(overrides: AtLeast<ICalendarEvent, 'event'>): ICalendarEvent {
    return {
      locked: false,
      isBlocking: true,
      title: [],
      notes: initVersionedSchema(),
      eventHistory: [],
      eventTags: [],
      ...initFirestoreModel(),
      ...overrides,
    };
  }

  static includesStaffer(
    calendarEvent: ICalendarEvent,
    stafferRef: DocumentReference<IStaffer>
  ): boolean {
    return calendarEvent.event.participants.some((participant) =>
      isSameRef(participant.ref, stafferRef)
    );
  }

  static participantsByType<T extends IStaffer | IPatient>(
    event: IEvent,
    type: ParticipantType
  ): IParticipant<T>[] {
    return event.participants.filter(
      (participant): participant is IParticipant<T> => participant.type === type
    );
  }

  static replaceEvent(
    calendarEvent: ICalendarEvent,
    newEvent: IEvent
  ): Pick<ICalendarEvent, 'event' | 'eventHistory'> {
    const lastEvent =
      calendarEvent.eventHistory[calendarEvent.eventHistory.length - 1];
    const isSameEvent: boolean = lastEvent
      ? lastEvent.event === calendarEvent.event
      : false;
    if (!isSameEvent) {
      const eventHistory = [
        ...calendarEvent.eventHistory,
        {
          createdAt: toTimestamp(),
          event: calendarEvent.event,
        },
      ];
      return {
        ...calendarEvent,
        eventHistory,
        event: newEvent,
      };
    }

    return {
      ...calendarEvent,
      eventHistory: calendarEvent.eventHistory,
      event: newEvent,
    };
  }

  static hasChanged(
    currentEvent: ICalendarEvent | WithRef<ICalendarEvent> | undefined,
    newEvent: ICalendarEvent | WithRef<ICalendarEvent>
  ): boolean {
    if (!currentEvent) {
      return true;
    }

    if (
      !currentEvent.event.from.isEqual(newEvent.event.from) ||
      !currentEvent.event.to.isEqual(newEvent.event.to)
    ) {
      return true;
    }

    if (
      isWithRef<ICalendarEvent>(currentEvent) &&
      !isWithRef<ICalendarEvent>(newEvent)
    ) {
      return true;
    }

    if (
      !isWithRef<ICalendarEvent>(currentEvent) &&
      isWithRef<ICalendarEvent>(newEvent)
    ) {
      return true;
    }

    if (
      isWithRef<ICalendarEvent>(currentEvent) &&
      isWithRef<ICalendarEvent>(newEvent) &&
      !isSameRef(currentEvent, newEvent)
    ) {
      return true;
    }

    if (
      !isWithRef<ICalendarEvent>(currentEvent) &&
      !isWithRef<ICalendarEvent>(newEvent) &&
      (!isSameRef(currentEvent.scheduleRef, newEvent.scheduleRef) ||
        !currentEvent.event.from.isEqual(newEvent.event.from))
    ) {
      return true;
    }

    return false;
  }

  static getCreatedBy(
    calendarEvent: ICalendarEvent | WithRef<ICalendarEvent>
  ): PrincipleCreatedBy | undefined {
    if (
      isWithRef<ICalendarEvent>(calendarEvent) &&
      !isString(calendarEvent.createdBy) &&
      isEnumValue(MentionResourceType, calendarEvent.createdBy?.type)
    ) {
      return calendarEvent.createdBy as PrincipleCreatedBy;
    }
    if (calendarEvent.event.creator) {
      return {
        ...calendarEvent.event.creator,
        type: MentionResourceType.Staffer,
      };
    }
    return;
  }

  static canEdit(
    event: IScheduleSummaryEventable | ICalendarEvent,
    staffer: WithRef<IStaffer>,
    hasBypassLockedPermission: boolean,
    userPermissions: Permission[]
  ): boolean {
    if (!CalendarEvent.canManageType(event.event.type, userPermissions)) {
      return false;
    }
    if (hasBypassLockedPermission) {
      return true;
    }
    if (isAppointmentRequest(event)) {
      return false;
    }

    const creator = isCalendarEvent(event)
      ? CalendarEvent.getCreatedBy(event)?.ref
      : event.metadata.createdBy?.ref;

    if (creator && isSameRef(creator, staffer)) {
      return true;
    }

    const isLocked = isCalendarEvent(event)
      ? event.locked
      : event.metadata.locked;
    return !isLocked;
  }

  static canManageType(
    type: EventType,
    userPermissions: Permission[]
  ): boolean {
    const requiredPermission = CALENDAR_EVENT_TYPE_PERMISSIONS[type];
    return !requiredPermission || userPermissions.includes(requiredPermission);
  }

  static filterCreatableTypes(
    types: EventType[],
    userPermissions: Permission[]
  ): EventType[] {
    return types.filter((type) =>
      CalendarEvent.canManageType(type, userPermissions)
    );
  }
}

export class CandidateCalendarEvent {
  static init(
    overrides: AtLeast<ICandidateCalendarEvent, 'candidate' | 'event'>
  ): ICandidateCalendarEvent {
    return {
      ...CalendarEvent.init({ event: overrides.event, isBlocking: false }),
      title: [toTextContent('Gap Candidate')],
      offerMade: false,
      ...overrides,
    };
  }
}

export function MockCalendarEvent(
  title: RawInlineNodes,
  range: ITimePeriod,
  type: EventType = EventType.Meeting,
  staffer: INamedDocument<IStaffer> = MockNamedDocument<IStaffer>(),
  overrides: Partial<ICalendarEvent> = {}
): WithRef<ICalendarEvent> {
  const participants = [{ ...staffer, type: ParticipantType.Staffer }];
  const participantRefs = participants.map((participant) => participant.ref);

  const calendarEvent: ICalendarEvent = {
    createdAt: toTimestamp(),
    deleted: false,
    event: {
      from: toTimestamp(range.from),
      to: toTimestamp(range.to),
      type,
      creator: staffer,
      organiser: staffer,
      participants,
      participantRefs,
      practice: MockNamedDocument<IPractice>('McMahons Point'),
    },
    eventTags: [],
    eventHistory: [],
    isBlocking: true,
    locked: false,
    notes: initVersionedSchema(),
    title,
    updatedAt: toTimestamp(),
    ...overrides,
  };
  return MockReffable(calendarEvent);
}

export function MockCandidateCalendarEvent(): WithRef<ICandidateCalendarEvent> {
  const range = { from: moment(), to: moment().add(1, 'hour') };
  const calendarEvent = MockCalendarEvent(
    [toTextContent('Gap Candidate')],
    range,
    EventType.GapCandidate
  );
  const candidate: ICandidate = {
    appointment: MockDocRef<IAppointment>(),
    patient: MockNamedDocument<IPatient>(),
    status: GapOfferStatus.Available,
    offerTimeFrom: MockTimestamp(),
    offerTimeTo: MockTimestamp(),
  };

  const candidateCalendarEvent = {
    ...calendarEvent,
    candidate,
    offerMade: false,
  };

  return MockReffable(candidateCalendarEvent);
}

export function calendarEventHasChanged(
  currentEvent: ICalendarEvent | WithRef<ICalendarEvent> | undefined,
  newEvent: ICalendarEvent | WithRef<ICalendarEvent>
): boolean {
  if (!currentEvent) {
    return true;
  }

  if (
    isWithRef<ICalendarEvent>(currentEvent) &&
    !isWithRef<ICalendarEvent>(newEvent)
  ) {
    return true;
  }

  if (
    !isWithRef<ICalendarEvent>(currentEvent) &&
    isWithRef<ICalendarEvent>(newEvent)
  ) {
    return true;
  }

  if (
    isWithRef<ICalendarEvent>(currentEvent) &&
    isWithRef<ICalendarEvent>(newEvent) &&
    !isSameRef(currentEvent, newEvent)
  ) {
    return true;
  }

  if (
    !isWithRef<ICalendarEvent>(currentEvent) &&
    !isWithRef<ICalendarEvent>(newEvent) &&
    (!isSameRef(currentEvent.scheduleRef, newEvent.scheduleRef) ||
      !currentEvent.event.from.isEqual(newEvent.event.from))
  ) {
    return true;
  }

  return false;
}
